import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const ASSIGNERS = ["founder", "managing_partner", "branch_manager", "sales_manager", "_legacy_branch_manager"];
export const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const inp = "w-full bg-muted/60 border h-9 px-2 text-sm";
const isMine = (t, user) => t.assignee_user_id === user?.user_id || (!t.assignee_user_id && !!user?.staff_name && t.assignee === user.staff_name);
const label = (a) => `${a.name}${a.staff_name && a.staff_name !== a.name ? ` (${a.staff_name})` : ""} · ${a.role_name}`;

const TaskList = ({ tasks, user, mayClose, onDone }) => (
  <div className="space-y-1 mb-4" data-testid="lead-task-list">
    {tasks.map((t) => (
      <div key={t.task_id} className="flex items-center gap-2 text-sm border-t py-1.5" data-testid={`lead-task-${t.task_id}`}>
        <span className="flex-1 min-w-0"><b>{t.assignee}</b> · {t.due_date}{t.note ? ` · ${t.note}` : ""}<span className="block text-[11px] text-muted-foreground">from {t.by_name}</span></span>
        {(mayClose || isMine(t, user)) && <button data-testid={`lead-task-done-${t.task_id}`} onClick={() => onDone(t.task_id)} className="border text-xs px-2 py-1 hover:border-primary">Done</button>}
      </div>))}
  </div>
);

const AssignForm = ({ people, f, setF, onSave }) => (
  <div className="space-y-3">
    <p className="text-xs text-muted-foreground">Give someone a follow-up on this lead. The lead's owner stays the same; the person gets an app alert and an email.</p>
    <label className="block text-xs"><span className="ax-label">Team member</span>
      <select data-testid="followup-assign-person" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} className={`${inp} mt-1`}>
        <option value="">Pick a person</option>{people.map((a) => <option key={a.user_id} value={a.user_id}>{label(a)}</option>)}</select></label>
    {!people.length && <p className="text-xs text-amber-700" data-testid="followup-assign-empty">No one with a sales login yet. Add people in Users & Access.</p>}
    <label className="block text-xs"><span className="ax-label">Follow up on</span>
      <input type="date" min={todayIST()} data-testid="followup-assign-date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={`${inp} mt-1`} /></label>
    <label className="block text-xs"><span className="ax-label">Note (optional)</span>
      <textarea data-testid="followup-assign-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="w-full bg-muted/60 border p-2 text-sm mt-1 h-20" placeholder="e.g. Share revised quote and ask for PO date" /></label>
    <button data-testid="followup-assign-save" disabled={!f.user_id || !f.due_date} onClick={onSave} className="bg-primary text-primary-foreground h-9 px-4 text-sm disabled:opacity-50">Assign</button>
  </div>
);

export const FollowupAssign = ({ leadId }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [f, setF] = useState({ user_id: "", due_date: todayIST(), note: "" });
  const can = ASSIGNERS.includes(user?.role_key);
  const load = useCallback(() => api.get(`/leads/${leadId}/followup-tasks`).then((r) => setTasks(r.data.filter((t) => t.status === "open"))).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open && can) api.get("/followup-assignees").then((r) => setPeople(r.data)).catch((e) => toast.error(errMsg(e))); }, [open, can]);
  if (!can && !tasks.length) return null;
  const who = people.find((a) => a.user_id === f.user_id);
  const save = () => api.post(`/leads/${leadId}/followup-tasks`, f).then(() => { toast.success(`Follow-up assigned to ${who?.name || "them"}`); setF({ ...f, user_id: "", note: "" }); load(); }).catch((e) => toast.error(errMsg(e)));
  const done = (id) => api.post(`/followup-tasks/${id}/done`).then(() => { toast.success("Marked done"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <>
      <button data-testid="lead-assign-followup-button" onClick={() => setOpen(true)} className="border h-9 px-3 text-sm flex items-center gap-1 whitespace-nowrap hover:border-primary">
        <CalendarPlus className="h-4 w-4" />{can ? "Assign follow-up" : "Follow-up task"}{tasks.length > 0 && <span className="num text-xs bg-primary text-primary-foreground rounded-full px-1.5">{tasks.length}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="followup-assign-dialog">
          <DialogHeader><DialogTitle>Follow-up tasks</DialogTitle></DialogHeader>
          {tasks.length > 0 && <TaskList tasks={tasks} user={user} mayClose={can} onDone={done} />}
          {can && <AssignForm people={people} f={f} setF={setF} onSave={save} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
